import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveMedia } from "../lib/api";
import { openWhatsApp } from "../lib/whatsapp";
import { ArrowLeft, Clock, MapPin, Check, Calendar as CalIcon } from "lucide-react";
import { InkFlourish, InkPaloma } from "../components/Ink";
import { useI18n } from "../i18n";

const MONTHS_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
const DAYS_ES   = ["domingo","lunes","martes","miércoles","jueves","viernes","sábado"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS_EN   = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

export default function EventDetail() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [translating, setTranslating] = useState(false);
  const { lang } = useI18n();

  useEffect(() => {
    setPageLoading(true);
    api.get(`/events/${id}`)
      .then((r) => setEv(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setPageLoading(false));
  }, [id]);

  // Load EN fields on demand so hero copy & details render in English.
  useEffect(() => {
    if (!ev || lang !== "en") return;
    if (ev.title_en && ev.description_en) return;
    (async () => {
      setTranslating(true);
      try {
        const r = await api.post(`/events/${ev.id}/translate?lang=en`);
        setEv(r.data);
      } catch {}
      finally { setTranslating(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ev?.id, lang]);

  if (pageLoading) {
    return <div className="max-w-4xl mx-auto px-6 py-24 text-subtle">Cargando…</div>;
  }
  if (notFound || !ev) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24" data-testid="event-not-found">
        <p className="eyebrow">Agenda</p>
        <h1 className="mt-3 font-serif text-4xl text-cobalt-deep">Evento no encontrado.</h1>
        <Link to="/calendario" className="mt-6 inline-block text-cobalt border-b border-cobalt pb-1 text-sm uppercase tracking-widest">Ver todos los eventos</Link>
      </div>
    );
  }

  const t = (field) => {
    if (lang === "en") {
      const en = ev[`${field}_en`];
      if (en !== undefined && en !== null && en !== "" && !(Array.isArray(en) && en.length === 0)) return en;
    }
    return ev[field];
  };

  const d = new Date(ev.date + "T00:00:00");
  const dateLabel = lang === "en"
    ? `${DAYS_EN[d.getDay()]}, ${MONTHS_EN[d.getMonth()]} ${d.getDate()}`
    : `${DAYS_ES[d.getDay()]} ${d.getDate()} de ${MONTHS_ES[d.getMonth()]}`;
  const L = lang === "en"
    ? { back: "Back to Calendar", date: "Date", time: "Time", location: "Location", includes: "What's included", price: "Price" }
    : { back: "Volver al calendario", date: "Fecha", time: "Hora", location: "Ubicación", includes: "Qué incluye", price: "Precio" };

  const handleCta = () => {
    if (ev.cta_type === "whatsapp") {
      openWhatsApp(`Hola. Me interesa "${ev.title}" el ${ev.date} a las ${ev.time}. ¿Me confirman disponibilidad?`);
    } else if (ev.cta_type === "external" && ev.cta_url) {
      window.open(ev.cta_url, "_blank", "noopener,noreferrer");
    }
  };
  const showCta = (ev.cta_type === "whatsapp" || (ev.cta_type === "external" && ev.cta_url)) && (t("cta_label") || ev.cta_label);
  const includes = t("includes") || [];
  const tagline = t("tagline");
  const description = t("description");
  const longDescription = t("long_description");

  const noTranslate = (en) => (lang === "en" && en) ? "true" : "false";

  return (
    <div className="bg-white" data-testid="event-detail-page">
      <section className="canvas-cream pt-10 pb-6 relative overflow-hidden">
        <InkPaloma className="hidden md:block absolute top-10 right-16 w-32 h-20 opacity-50" stroke="#1F3A6E" />
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <Link to="/calendario" className="inline-flex items-center gap-2 text-[0.68rem] tracking-[0.26em] uppercase text-cobalt hover:text-terracotta font-display font-semibold" data-testid="event-back">
            <ArrowLeft size={14} /> {L.back}
          </Link>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-5xl mx-auto">
          {ev.image && (
            <img src={resolveMedia(ev.image)} alt={t("title")} className="w-full h-[46vh] sm:h-[60vh] object-cover" />
          )}
          <div className="px-6 sm:px-10 lg:px-16 py-14">
            <p className="eyebrow">{ev.category}</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05]" data-testid="event-title" data-no-translate={noTranslate(ev.title_en)}>{t("title")}</h1>
            {tagline && <p className="mt-5 script text-3xl text-cobalt" data-no-translate={noTranslate(ev.tagline_en)}>{tagline}</p>}
            {description && <p className="mt-6 text-[1.05rem] text-muted2 leading-relaxed max-w-2xl" data-no-translate={noTranslate(ev.description_en)}>{description}</p>}
            {translating && lang === "en" && !ev.description_en && (
              <p className="mt-6 text-subtle italic">Translating…</p>
            )}
            {longDescription && (
              <div className="mt-6 text-[1.02rem] text-muted2 leading-[1.9] max-w-2xl whitespace-pre-line" data-testid="event-long-description" data-no-translate={noTranslate(ev.long_description_en)}>{longDescription}</div>
            )}

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 border-y border-cobalt/30 py-8">
              <div>
                <p className="eyebrow">{L.date}</p>
                <p className="mt-2 text-ink inline-flex items-center gap-2 capitalize" data-no-translate="true"><CalIcon size={16}/> {dateLabel}</p>
              </div>
              <div>
                <p className="eyebrow">{L.time}</p>
                <p className="mt-2 text-ink inline-flex items-center gap-2" data-no-translate="true"><Clock size={16}/> {ev.time}</p>
              </div>
              <div>
                <p className="eyebrow">{L.location}</p>
                <p className="mt-2 text-ink inline-flex items-center gap-2" data-no-translate={noTranslate(ev.location_en)}><MapPin size={16}/> {t("location")}</p>
              </div>
            </div>

            {includes && includes.length > 0 && (
              <div className="mt-10">
                <p className="eyebrow">{L.includes}</p>
                <ul className="mt-4 space-y-2 max-w-xl" data-testid="event-includes" data-no-translate={noTranslate(ev.includes_en && ev.includes_en.length)}>
                  {includes.map((inc, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted2"><Check size={16} className="mt-1 text-cobalt shrink-0"/> {inc}</li>
                  ))}
                </ul>
              </div>
            )}

            <InkFlourish className="w-32 h-4 mt-14 opacity-60" />

            <div className="mt-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-t border-cobalt/30 pt-8">
              {ev.price ? (
                <div>
                  <p className="eyebrow">{L.price}</p>
                  <p className="font-serif text-2xl text-ink mt-1" data-no-translate="true">{ev.price}</p>
                </div>
              ) : <div />}
              {showCta && (
                <button className="btn-primary" onClick={handleCta} data-testid="event-cta" data-no-translate={noTranslate(ev.cta_label_en)}>{t("cta_label") || ev.cta_label}</button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
